import { useAbility } from "@casl/react";
import { Link, useNavigate } from "@tanstack/react-router";
import { type FC, useState } from "react";

import type { AppAbility } from "@recobra/core";
import { m } from "@recobra/i18n/messages";
import type { NotificationFilter } from "@recobra/schemas/notification";
import { Badge } from "@recobra/ui/badge";
import { Button } from "@recobra/ui/button";
import { ScrollArea } from "@recobra/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@recobra/ui/tabs";

import {
  NotificationInboxHeader,
  notificationTabCount,
  notificationTabLabel,
} from "./NotificationInboxHeader";
import { NotificationList } from "./NotificationList";
import { navigateToNotificationTarget } from "./resolveNotificationNavigation";
import { useNotificationMutations } from "./useNotificationMutations";
import { useNotifications } from "./useNotifications";
import { useNotificationSummary } from "./useNotificationSummary";

const FILTERS: NotificationFilter[] = ["all", "unread", "mention", "system"];

export const NotificationInbox: FC<{
  previewLimit?: number;
  showFooter?: boolean;
  showViewAll?: boolean;
}> = ({ previewLimit, showFooter = true, showViewAll = false }) => {
  const [filter, setFilter] = useState<NotificationFilter>("all");
  const navigate = useNavigate();
  const ability = useAbility<AppAbility>();
  const { data: summary } = useNotificationSummary();
  const query = useNotifications(filter);
  const { markRead, markAllRead, clearRead } = useNotificationMutations();

  const items = (query.data?.items ?? []).slice(0, previewLimit ?? Number.POSITIVE_INFINITY);

  const tabCount = (value: NotificationFilter) => notificationTabCount(value, summary);

  return (
    <div className="flex min-h-0 flex-col">
      <NotificationInboxHeader
        unreadCount={summary?.unread ?? 0}
        readCount={summary?.read ?? 0}
        onMarkAllRead={() => markAllRead.mutate()}
        onClearRead={() => clearRead.mutate()}
        markAllPending={markAllRead.isPending}
        clearPending={clearRead.isPending}
      />

      <Tabs
        value={filter}
        onValueChange={(value) => setFilter(value as NotificationFilter)}
        className="min-h-0 flex-1"
      >
        <div className="border-b px-3">
          <TabsList variant="line" className="w-full justify-start">
            {FILTERS.map((value) => {
              const count = tabCount(value);
              const label = notificationTabLabel(value);

              return (
                <TabsTrigger key={value} value={value}>
                  {label}
                  {count ? <Badge>{count}</Badge> : null}
                </TabsTrigger>
              );
            })}
          </TabsList>
        </div>

        {FILTERS.map((value) => (
          <TabsContent key={value} value={value} className="mt-0 min-h-0 flex-1">
            <ScrollArea className={previewLimit ? "h-104" : "h-[min(36rem,calc(100svh-14rem))]"}>
              <NotificationList
                items={items}
                filter={value}
                isLoading={query.isLoading}
                onMarkRead={(id) => markRead.mutate(id)}
                onNavigate={(target, id) => {
                  markRead.mutate(id);
                  navigateToNotificationTarget(navigate, target, ability.can("readAll", "Task"));
                }}
              />
            </ScrollArea>
          </TabsContent>
        ))}
      </Tabs>

      {showFooter && summary ? (
        <div className="flex items-center justify-between border-t px-3 py-2 text-xs text-muted-foreground/80">
          <span>{m.notifications_footer_total({ count: summary.total })}</span>
          <span>
            {m.notifications_footer_breakdown({
              unread: summary.unread,
              read: summary.read,
            })}
          </span>
        </div>
      ) : null}

      {showViewAll ? (
        <div className="border-t px-3 py-2">
          <Button
            variant="outline"
            size="sm"
            className="w-full"
            render={<Link to="/notifications" />}
          >
            {m.notifications_view_all()}
          </Button>
        </div>
      ) : null}
    </div>
  );
};
